import { useGet } from '@resistapp/client/hooks/api';
import {
  AdminGenePanel,
  AdminGenePanelsResponse,
  AdminOrganisationSampleRow,
  AdminOrganisationSamplesResponse,
} from '@resistapp/common/api-types';
import { formatSampleTypeLabel, formatSamplingPointSubTypeLabel } from '@resistapp/common/sample-sheet';
import { useMemo } from 'react';

import { type LabWorkbookSummary } from './use-lab-workbook';

const TECH_REPLICA_COUNT = 3;

type MaybeNumber = number | null | undefined;

function toError(value: unknown): Error | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    return new Error(value);
  }
  return new Error('Unknown error');
}

export type LabTechRepRow = {
  order: number;
  chipNumber: number;
  techRep: string;
  bioSampleId: number | null;
  sampleLabel: string;
  replicateLabel: string;
  sampleDescription: string | null;
};

export type LabTechRepBucket = {
  chipNumber: number;
  rows: LabTechRepRow[];
};

export type LabTubePickRow = {
  order: number;
  chipNumber: number;
  sampleId: number;
  sampleLabel: string;
  sampleDescription: string | null;
  sampleTypeLabel: string | null;
  sampleSubtypeLabel: string | null;
  primDilutedDnaConcentration: number | null;
  primDilutedDnaQuality280: number | null;
  primDilutedDnaQuality230: number | null;
  primElutedDnaConcentration: number | null;
  filteredVolume: number | null;
};

export type LabSamplePlatesData = {
  chips: LabTechRepBucket[];
  tubePickList: LabTubePickRow[];
  samplesPerChip: number | null;
  replicatesPerSample: number;
  chipCount: number;
  totalSamples: number;
};

export type UseLabSamplePlatesResult = {
  data: LabSamplePlatesData | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  samples?: AdminOrganisationSamplesResponse;
  genePanels?: AdminGenePanelsResponse;
};

function sanitizeNumber(value: MaybeNumber): number | null {
  if (value == null) {
    return null;
  }
  return Number.isFinite(value) ? Number(value) : null;
}

function buildSampleLabel(row: AdminOrganisationSampleRow): string {
  const numberPart = row.sampleNumber != null ? `${row.sampleNumber}` : '';
  const bioReplicaPart = row.bioReplica != null ? String(row.bioReplica) : '';
  if (!numberPart && !bioReplicaPart) {
    return '';
  }
  return `${numberPart}${bioReplicaPart}`;
}

function buildTechRepLabel(row: AdminOrganisationSampleRow, replicateIndex: number): string {
  const baseLabel = buildSampleLabel(row);
  const replicate = replicateIndex >= 1 ? `rep${replicateIndex}` : 'rep';
  if (row.sampleId) {
    const labelCore = baseLabel ? `-${baseLabel}` : '';
    return `${row.sampleId}${labelCore}-${replicate}`;
  }
  return baseLabel ? `${baseLabel}-${replicate}` : replicate;
}

function getPanelSamplesCount(panel: AdminGenePanel | undefined): number | null {
  if (!panel) {
    return null;
  }
  const parsed = Number(panel.samples);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function ensurePositive(value: number | null | undefined, fallback: number): number {
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return value;
}

export function useLabSamplePlates(summary: LabWorkbookSummary | undefined): UseLabSamplePlatesResult {
  const organisationId = summary?.sampleSheet.organisationId;
  const genePanelId = summary?.workbook.genePanelId;

  const workbookId = summary?.workbook.id ?? null;
  const samplesQuery = useGet<AdminOrganisationSamplesResponse>(
    workbookId ? `/lab/${workbookId}/samples` : '/lab/_skip',
    { enabled: Boolean(workbookId) },
  );

  const shouldFetchPanels = organisationId != null && genePanelId != null;
  const panelsQuery = useGet<AdminGenePanelsResponse>(
    shouldFetchPanels ? `admin/organisation-panels/${organisationId}` : '',
    {
      enabled: shouldFetchPanels,
      staleTime: 30_000,
    },
  );

  const error = toError(samplesQuery.error) ?? toError(panelsQuery.error);

  const data = useMemo<LabSamplePlatesData | null>(() => {
    if (!summary || !samplesQuery.data) {
      return null;
    }

    // Include all rows linked to the sheet. Some sample sheets may not propagate
    // per‑sheet sample numbers into the base SampleModel; relying on only rows
    // with `sampleNumber` would hide valid submitted rows. Filter out placeholder
    // shells where only the UID exists and no descriptive data is present.
    const filteredRows = samplesQuery.data.rows.filter(r => r.sampleNumber != null);

    if (filteredRows.length === 0) {
      return {
        chips: [],
        tubePickList: [],
        samplesPerChip: null,
        replicatesPerSample: TECH_REPLICA_COUNT,
        chipCount: 0,
        totalSamples: 0,
      };
    }

    const orderedRows = filteredRows;

    const panel = panelsQuery.data?.panels.find(candidate => candidate.id === genePanelId);
    const samplesPerChip = getPanelSamplesCount(panel);
    const normalizedSamplesPerChip = ensurePositive(samplesPerChip, Math.max(orderedRows.length, 1));

    const techRepBuckets = new Map<number, LabTechRepRow[]>();

    orderedRows.forEach((row, sampleIndex) => {
      const chipNumber = Math.floor(sampleIndex / normalizedSamplesPerChip) + 1;
      const bucket = techRepBuckets.get(chipNumber) ?? [];
      const sampleLabel = buildSampleLabel(row);
      for (let replicateIndex = 1; replicateIndex <= TECH_REPLICA_COUNT; replicateIndex += 1) {
        const techRep = buildTechRepLabel(row, replicateIndex);
        bucket.push({
          order: bucket.length + 1,
          chipNumber,
          techRep,
          bioSampleId: row.sampleId,
          sampleLabel,
          replicateLabel: `rep${replicateIndex}`,
          sampleDescription: row.sampleDescription ?? null,
        });
      }
      techRepBuckets.set(chipNumber, bucket);
    });

    const chips: LabTechRepBucket[] = Array.from(techRepBuckets.entries())
      .sort((left, right) => left[0] - right[0])
      .map(([chipNumber, bucketRows]) => ({
        chipNumber,
        rows: bucketRows,
      }));

    const tubePickList: LabTubePickRow[] = orderedRows.map((row, index) => {
      const chipNumber = Math.floor(index / normalizedSamplesPerChip) + 1;
      const sampleLabel = buildSampleLabel(row);
      return {
        order: index + 1,
        chipNumber,
        sampleId: row.sampleId,
        sampleLabel,
        sampleDescription: row.sampleDescription ?? null,
        sampleTypeLabel: row.sampleType ? formatSampleTypeLabel(row.sampleType) : null,
        sampleSubtypeLabel: row.detailedSampleType
          ? formatSamplingPointSubTypeLabel(row.detailedSampleType, row.sampleType)
          : null,
        primDilutedDnaConcentration: sanitizeNumber(row.primDilutedDnaConcentration),
        primDilutedDnaQuality280: sanitizeNumber(row.primDilutedDnaQuality280),
        primDilutedDnaQuality230: sanitizeNumber(row.primDilutedDnaQuality230),
        primElutedDnaConcentration: sanitizeNumber(row.primElutedDnaConcentration),
        filteredVolume: sanitizeNumber(row.filteredVolume),
      };
    });

    const chipCount = chips.length;

    return {
      chips,
      tubePickList,
      samplesPerChip,
      replicatesPerSample: TECH_REPLICA_COUNT,
      chipCount,
      totalSamples: orderedRows.length,
    };
  }, [genePanelId, panelsQuery.data, samplesQuery.data, summary]);

  const isLoading = samplesQuery.isLoading || panelsQuery.isLoading;
  const isFetching = samplesQuery.isFetching || panelsQuery.isFetching;
  const isError = Boolean(error);

  return {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    samples: samplesQuery.data,
    genePanels: panelsQuery.data,
  };
}
